import React from 'react';

import cn from 'classnames';
import { isFunction, isString } from 'lodash';
import { bool, func, node, oneOf, oneOfType, shape, string } from 'prop-types';

import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import Button from 'components/Common/Buttons/ButtonV3';

import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import Snackbar from 'components/Common/Snackbar';
import { BACKDROP_CLICK } from 'constants/commonComponents';
import useMediaInfo from 'hooks/useMediaInfo';

import s from './Modal.css';

const Modal = props => {
  const {
    title,
    onClose,
    classes,
    dialogClasses,
    children,
    actions,
    open,
    hideBackButton,
    scroll,
    fullscreen,
    confirmationButtonText,
    confirmationText,
    noHeader,
    disableClose,
    BackdropProps,
    disableEnforceFocus,
    actionsClassName,
    dataTtsParentId,
    isElementary,
  } = props;

  const { isMobile } = useMediaInfo();
  const isFullscreen = isMobile || fullscreen;
  const showBackButton =
    !hideBackButton && isFunction(onClose) && !disableClose;
  const elButtonProps = isElementary
    ? {
        isElementary: true,
        variant: 'outlined',
        size: 'small',
        color: 'gray',
      }
    : {};

  const dialogHeader = isFullscreen ? (
    <div className={cn(s.mobileHeader, classes.mobileHeader)}>
      <>
        {showBackButton && (
          <Button
            className={s.backButton}
            color="gray"
            confirmationButtonText={confirmationButtonText}
            confirmationText={confirmationText}
            hasIconLeft
            icon={<FontAwesomeIcon icon={faXmark} />}
            onClick={onClose}
            aria-label="Close modal"
            {...elButtonProps}
          />
        )}
        <div
          className={cn(s.title, classes.title, {
            [s.ellipsis]: isString(title),
          })}
        >
          {title}
        </div>
      </>
    </div>
  ) : (
    <div className={cn(s.header, classes.desktopHeader)}>
      <div className={s.title}>{title}</div>
      {isFunction(onClose) && !disableClose && (
        <Button
          className={s.topRightCloseButton}
          confirmationButtonText={confirmationButtonText}
          confirmationText={confirmationText}
          hasIconLeft
          icon={<FontAwesomeIcon icon={faXmark} />}
          onClick={onClose}
          aria-label="Close modal"
          {...elButtonProps}
        />
      )}
    </div>
  );

  return (
    <Dialog
      disableEnforceFocus={disableEnforceFocus}
      BackdropProps={BackdropProps}
      fullScreen={isFullscreen}
      onClose={(_, reason) => {
        if (reason === BACKDROP_CLICK || disableClose) {
          return;
        }
        onClose();
      }}
      open={open}
      classes={{
        ...classes,
        paper: cn(s.paperContainer, classes?.paper),
      }}
      scroll={scroll}
      maxWidth={false}
      data-tts-parent-id={dataTtsParentId}
    >
      {!noHeader ? dialogHeader : null}
      <DialogContent classes={{ root: s.rootContainer, ...dialogClasses }}>
        {children}
      </DialogContent>
      {actions && (
        <DialogActions className={cn(s.actions, actionsClassName)}>
          {actions}
        </DialogActions>
      )}
      <Snackbar />
    </Dialog>
  );
};

Modal.propTypes = {
  title: oneOfType([string, node]).isRequired,
  children: node.isRequired,
  actions: node,
  onClose: func.isRequired,
  classes: shape({}),
  dialogClasses: shape({}),
  open: bool,
  hideBackButton: bool,
  // Determine the container for scrolling the dialog.
  scroll: oneOf(['paper', 'body']),
  // Enable fullscreen even if screen size is not mobile
  fullscreen: bool,
  confirmationText: string,
  confirmationButtonText: string,
  noHeader: bool,
  // if true, modal cannot be closed and will not render close button
  disableClose: bool,
  // if true, elements in background are interactive -- should be used VERY sparingly as this presents accessibility challenges
  disableEnforceFocus: bool,
  // https://mui.com/material-ui/api/backdrop/
  BackdropProps: shape({}),
  actionsClassName: node,
  dataTtsParentId: string,
  isElementary: bool,
};

Modal.defaultProps = {
  actions: null,
  classes: {},
  dialogClasses: {},
  open: true,
  hideBackButton: false,
  scroll: 'paper',
  fullscreen: false,
  confirmationText: undefined,
  confirmationButtonText: undefined,
  noHeader: false,
  disableClose: false,
  disableEnforceFocus: false,
  BackdropProps: undefined,
  actionsClassName: undefined,
  dataTtsParentId: undefined,
  isElementary: false,
};

export default Modal;
